import VisibilityScope, { VisibilityScopeValue } from "../../../../ui/VisibilityScope";
import { FC, useContext, useEffect, useState } from "react";
import { Controller } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import NotificationDispatch, { showErrorNotification } from "../../../../../context/notificationContext";
import useI18n from "../../../../../hooks/useTranslations";
import { InsertGuardrailCompositionBody } from "../../../../../http/border/types/guardrailCompositions";
import { useGuardrailCompositionsApi } from "../../../../../http/border/useGuardrailCompositions";
import Form from "../../../../ui/Form";
import FormField from "../../../../ui/FormField";
import FormFieldsContainer from "../../../../ui/FormFieldsContainer";
import Input from "../../../../ui/Input";
import Numeric from "../../../../ui/Numeric";
import Switch from "../../../../ui/Switch";
import TranslatableField from "../../../../ui/TranslatableField";

interface InsertGuardrailCompositionForm {
  name: string | null;
  nameIt: string | null;
  nameDe: string | null;
  nameEs: string | null;
  nameFr: string | null;
  order: number | null;
  visibility: VisibilityScopeValue;
  isIntermediateBarRequired: boolean;
  isToeBoardRequired: boolean;
}

const NewGuardrailComposition: FC = () => {
  const { systemId } = useParams<{ systemId: string }>();
  const navigate = useNavigate();
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const guardrailCompositionsApi = useGuardrailCompositionsApi();

  const onSubmit = () => async (formData: InsertGuardrailCompositionForm) => {
    const body: InsertGuardrailCompositionBody = {
      name: formData.name!,
      nameIt: formData.nameIt?.trim() ?? null,
      nameDe: formData.nameDe?.trim() ?? null,
      nameEs: formData.nameEs?.trim() ?? null,
      nameFr: formData.nameFr?.trim() ?? null,
      order: formData.order!,
      visibility: formData.visibility,
      isIntermediateBarRequired: formData.isIntermediateBarRequired,
      isToeBoardRequired: formData.isToeBoardRequired,
    };

    setIsSubmitting(true);

    try {
      const res = await guardrailCompositionsApi.insertAdminGuardrailComposition(body);
      navigate(`/admin/systems/${systemId}/guardrail_compositions/${res.id}`);
    } catch (error) {
      dispatch(showErrorNotification(i18n, error));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form<InsertGuardrailCompositionForm>
      defaultValues={{
        name: null,
        nameIt: null,
        nameDe: null,
        nameEs: null,
        nameFr: null,
        order: 50,
        visibility: "hidden",
        isIntermediateBarRequired: false,
        isToeBoardRequired: false,
      }}
      submitText={i18n.translation.common.save}
      disabled={isSubmitting}
      onSubmit={onSubmit}
    >
      {({ register, formState: { errors }, control, setValue, watch }) => {
        const isIntermediateBarRequired = !!watch("isIntermediateBarRequired");
        const isToeBoardRequired = !!watch("isToeBoardRequired");

        useEffect(() => {
          register("nameIt");
          register("nameDe");
          register("nameEs");
          register("nameFr");
        }, [register]);

        const values = watch();

        return (
          <FormFieldsContainer fullWidth>
            <FormField label={i18n.translation.common.name} error={errors.name}>
              {({ labelId, isInvalid, isOptional }) => (
                <TranslatableField
                  field="name"
                  formValues={values}
                  setFormValue={setValue}
                  dialogTitle={i18n.translation.common.name}
                >
                  <Input
                    id={labelId}
                    isInvalid={isInvalid}
                    disabled={isSubmitting}
                    {...register("name", {
                      required: !isOptional,
                      minLength: 1,
                    })}
                  />
                </TranslatableField>
              )}
            </FormField>

            <FormField label={i18n.translation.common.isIntermediateBarRequired} isOptional>
              {({ labelId }) => (
                <Switch
                  id={labelId}
                  checked={isIntermediateBarRequired}
                  disabled={isSubmitting}
                  onChange={(checked) => setValue("isIntermediateBarRequired", checked)}
                />
              )}
            </FormField>

            <FormField label={i18n.translation.common.isToeBoardRequired} isOptional>
              {({ labelId }) => (
                <Switch
                  id={labelId}
                  checked={isToeBoardRequired}
                  disabled={isSubmitting}
                  onChange={(checked) => setValue("isToeBoardRequired", checked)}
                />
              )}
            </FormField>
            <Controller
              name="visibility"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.visibility} error={fieldState.error} isOptional>
                  {({ labelId }) => (
                    <VisibilityScope
                      {...field}
                      inputId={labelId}
                      isDisabled={isSubmitting}
                      onChange={(value) => setValue("visibility", value)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="order"
              control={control}
              rules={{ required: true, min: 1 }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.order} error={fieldState.error}>
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      {...field}
                      id={labelId}
                      isInvalid={isInvalid}
                      decimalScale={0}
                      allowNegative={false}
                      disabled={isSubmitting}
                      onBlur={(value) => setValue("order", value)}
                    />
                  )}
                </FormField>
              )}
            />
          </FormFieldsContainer>
        );
      }}
    </Form>
  );
};

export default NewGuardrailComposition;
